import { phCapture } from '@/lib/analytics/posthog';
import type { BookOrderStatus } from './types';

/**
 * Shared `book_order_paid` fire-once helper (Sebastian ruling 2026-10-22).
 *
 * Pre-ruling the sentinel + PostHog call lived inside
 * `BestellungErfolgRoute.tsx` only. That route only reached the
 * success-flow when a Stripe `session_id` landed on the redirect —
 * if the customer closed the Stripe tab mid-webhook, authenticated
 * elsewhere, or just reloaded `/book/:orderId` while the backend was
 * already paid, the `/erfolg` route never ran and `book_order_paid`
 * never fired. The post-payment funnel then undercounted the paid
 * orders this screen does catch.
 *
 * Fix: lift the sentinel + fire into one helper reused by both the
 * `/erfolg` success flow (unchanged payload, carries `sales_unit`) AND
 * the `/book/:orderId` acknowledgment path (invoked when the envelope
 * lands in a paid-or-beyond status with no sentinel). Same localStorage
 * key, same per-order dedup, so the event fires AT MOST once per order
 * across both routes.
 *
 * Why localStorage, not sessionStorage: payment ↔ book-open frequently
 * spans sessions (Stripe redirect kills the tab, the customer returns
 * via mail hours later). Session storage would re-fire on every return
 * visit after each tab close.
 */

/**
 * Per-order sentinel key. Keep lowercase and underscore-only — mixed
 * casing across the two call sites would silently double-fire on orders
 * whose UUID contains uppercase.
 */
export function bookOrderPaidSentinelKey(orderId: string): string {
  return `book_order_paid_${orderId}`;
}

/**
 * Paid-or-beyond status set (mirrors `src/admin/book-reporting/book-reporting
 * .constants.ts` PAID_OR_BEYOND_STATUSES). Any backend status at or past
 * `paid` counts as "payment acknowledged" for this helper's purposes —
 * the acknowledgment is "the money arrived," not "the book is finished."
 *
 * `qa_regen` + `qa_human_queue` are included because they are reachable
 * only AFTER `rendering_full`/`qa_running`, so the paid signal has
 * already landed by the time they appear.
 */
export const BOOK_PAID_OR_BEYOND_STATUSES: readonly BookOrderStatus[] = [
  'paid',
  'rendering_full',
  'qa_running',
  'qa_regen',
  'qa_human_queue',
  'customer_review',
  'print_approved',
  'prepress',
  'handoff_pod',
  'shipped',
  'delivered',
];

export function isBookPaidOrBeyond(status: BookOrderStatus): boolean {
  return BOOK_PAID_OR_BEYOND_STATUSES.includes(status);
}

/**
 * Narrower gate the `/book/:orderId` acknowledgment effect uses to decide
 * whether to fire `book_order_paid` (Sebastian ruling 2026-10-27).
 *
 * Why narrower than `BOOK_PAID_OR_BEYOND_STATUSES`: BookRoute does not
 * know WHEN the order became paid — the envelope carries no `paidAt`
 * (confirmed: `BookOrder` has no `paidAt` column; the admin export
 * uses `updatedAt` as a proxy, see
 * `src/book/checkout/book-checkout.service.ts:253` and
 * `src/admin/cli/commands/book-orders-export.command.ts:64-70`). If
 * BookRoute fired on every paid-or-beyond status, a customer opening a
 * long-shipped book weeks later (e.g. from the review mail in a new
 * browser) would emit `book_order_paid` with today's date — a second,
 * retro-dated conversion event with no basis in reality.
 *
 * The cut-off is `customer_review` (inclusive). Every status in this
 * list is reachable BEFORE the customer clicks "Freigabe" (release for
 * print). After release the order moves to `print_approved` and the
 * payment moment is no longer plausibly "fresh" from the customer's
 * perspective — the book was approved, printed, maybe already shipped.
 * `/erfolg` is still the authoritative first-fire site when the Stripe
 * hop just happened; BookRoute's job is only to catch same-session
 * bypasses (closed Stripe tab mid-webhook, auth elsewhere, hard-reload
 * before `/erfolg` finished polling) — all of which land the customer
 * in these pre-release statuses, never post-release.
 *
 * The sentinel semantics are unchanged — the key still writes once per
 * orderId; a user reaching the book in `shipped` after a prior
 * `/erfolg` fire still shows the sentinel key as "1". This gate is a
 * NO-FIRE filter at the call site, not a sentinel-write filter.
 */
export const BOOK_ROUTE_FRESH_PAID_FIRE_STATUSES: readonly BookOrderStatus[] = [
  'paid',
  'rendering_full',
  'qa_running',
  'qa_regen',
  'qa_human_queue',
  'customer_review',
];

export function isBookRouteFreshPaidFireStatus(status: BookOrderStatus): boolean {
  return BOOK_ROUTE_FRESH_PAID_FIRE_STATUSES.includes(status);
}

/**
 * Fire `book_order_paid` exactly once per `orderId` across the PWA.
 *
 * `salesUnit` is passed through to the event properties:
 *   - `/erfolg` success flow (Stripe path): populated from the checkout-
 *     status response.
 *   - `/erfolg` success flow (fully-covered balance path): `null`.
 *   - `/book/:orderId` acknowledgment path: `null` (the shell envelope
 *     does not expose salesUnit; the Stripe/balance split is already
 *     visible to the backend via amount tracking).
 *
 * Returns `true` if the event was fired on THIS call (and the sentinel
 * written). `false` if the sentinel was already set OR PostHog was not
 * initialised (statistics consent not granted — the event will fire the
 * next time this helper is called with the sentinel still absent).
 *
 * Storage + capture errors are swallowed. The absolute invariant is
 * no double-fire; a failure to record is a muted loss, never a
 * duplicate.
 */
export function fireBookOrderPaidOnce(orderId: string, salesUnit: string | null): boolean {
  try {
    const key = bookOrderPaidSentinelKey(orderId);
    if (window.localStorage.getItem(key) === '1') return false;
    if (phCapture('book_order_paid', { book_order_id: orderId, sales_unit: salesUnit })) {
      window.localStorage.setItem(key, '1');
      return true;
    }
    return false;
  } catch {
    return false;
  }
}
